import api from './api';

export interface ReportData {
  id: string;
  name: string;
  type: 'security_events' | 'leaked_credentials' | 'digital_assets' | 'domain_investigation';
  status: 'generating' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
  data?: any;
  file_url?: string;
}

export interface SecurityEventsReportData {
  summary: {
    total_events: number;
    critical_events: number;
    high_events: number;
    medium_events: number;
    low_events: number;
    new_events: number;
    resolved_events: number;
  };
  by_severity: Array<{
    severity: string;
    count: number;
    percentage: number;
  }>;
  by_type: Array<{
    type: string;
    count: number;
    percentage: number;
  }>;
  by_source: Array<{
    source: string;
    count: number;
    percentage: number;
  }>;
  by_status: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  timeline: Array<{
    date: string;
    count: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  }>;
  recent_events: Array<{
    id: number;
    title: string;
    severity: string;
    type: string;
    source: string;
    created_at: string;
    status: string;
  }>;
}

export interface LeakedCredentialsReportData {
  summary: {
    total_credentials: number;
    unique_emails: number;
    unique_databases: number;
    high_severity: number;
    critical_severity: number;
  };
  by_severity: Array<{
    severity: string;
    count: number;
  }>;
  by_database: Array<{
    database: string;
    count: number;
  }>;
  by_domain: Array<{
    domain: string;
    count: number;
  }>;
  timeline: Array<{
    date: string;
    count: number;
  }>;
}

export interface ReportGenerationRequest {
  type: 'security_events' | 'leaked_credentials' | 'digital_assets' | 'domain_investigation';
  name: string;
  date_range: {
    start: string;
    end: string;
  };
  filters?: {
    severity?: string[];
    sources?: string[];
    event_types?: string[];
  };
  format: 'json' | 'pdf' | 'excel';
}

class ReportsService {
  // Gerar relatório de eventos de segurança
  async generateSecurityEventsReport(request: ReportGenerationRequest): Promise<ReportData> {
    const response = await api.post('/api/v2/reports/security-events', request);
    return response.data;
  }

  // Buscar dados para relatório de eventos de segurança
  async getSecurityEventsData(dateRange: { start: string; end: string }): Promise<SecurityEventsReportData> {
    // Buscar dados do dashboard e eventos
    const [overviewResponse, eventsResponse, distributionResponse] = await Promise.all([
      api.get('/api/v2/dashboard/overview'),
      api.get(`/api/v2/events?start_date=${dateRange.start}&end_date=${dateRange.end}&limit=10`),
      api.get('/api/v2/dashboard/events/distribution?days=30'),
    ]);

    const overview = overviewResponse.data;
    const events = eventsResponse.data;
    const distribution = distributionResponse.data;

    // Processar dados para o relatório
    const summary = {
      total_events: overview.events?.total || 0,
      critical_events: overview.events?.critical || 0,
      high_events: overview.events?.high || 0,
      medium_events: overview.events?.medium || 0,
      low_events: overview.events?.low || 0,
      new_events: overview.events?.new || 0,
      resolved_events: overview.events?.resolved || 0,
    };

    // Converter distribuição por severidade
    const by_severity = Object.entries(distribution.by_severity || {}).map(([severity, count]) => ({
      severity,
      count: count as number,
      percentage: summary.total_events > 0 ? ((count as number) / summary.total_events) * 100 : 0,
    }));

    // Converter distribuição por tipo
    const by_type = Object.entries(distribution.by_type || {}).map(([type, count]) => ({
      type,
      count: count as number,
      percentage: summary.total_events > 0 ? ((count as number) / summary.total_events) * 100 : 0,
    }));

    // Converter distribuição por fonte
    const by_source = Object.entries(distribution.by_source || {}).map(([source, count]) => ({
      source,
      count: count as number,
      percentage: summary.total_events > 0 ? ((count as number) / summary.total_events) * 100 : 0,
    }));

    // Converter distribuição por status
    const by_status = Object.entries(distribution.by_status || {}).map(([status, count]) => ({
      status,
      count: count as number,
      percentage: summary.total_events > 0 ? ((count as number) / summary.total_events) * 100 : 0,
    }));

    // Timeline dos últimos 30 dias
    const timeline = distribution.timeline || [];

    // Eventos recentes
    const recent_events =
      events.items?.slice(0, 10).map((event: any) => ({
        id: event.id,
        title: event.title,
        severity: event.severity,
        type: event.event_type,
        source: event.source,
        created_at: event.created_at,
        status: event.status,
      })) || [];

    return {
      summary,
      by_severity,
      by_type,
      by_source,
      by_status,
      timeline,
      recent_events,
    };
  }

  // Buscar dados para relatório de credenciais vazadas
  async getLeakedCredentialsData(dateRange: { start: string; end: string }): Promise<LeakedCredentialsReportData> {
    const statsResponse = await api.get('/api/v2/leaked-credentials/stats');
    const stats = statsResponse.data;

    return {
      summary: {
        total_credentials: stats.total_credentials || 0,
        unique_emails: stats.unique_emails || 0,
        unique_databases: stats.unique_databases || 0,
        high_severity: stats.by_severity?.high || 0,
        critical_severity: stats.by_severity?.critical || 0,
      },
      by_severity: Object.entries(stats.by_severity || {}).map(([severity, count]) => ({
        severity,
        count: count as number,
      })),
      by_database: Object.entries(stats.by_database || {}).map(([database, count]) => ({
        database,
        count: count as number,
      })),
      by_domain: Object.entries(stats.by_domain || {}).map(([domain, count]) => ({
        domain,
        count: count as number,
      })),
      timeline: stats.timeline || [],
    };
  }

  // Listar relatórios existentes
  async getReports(): Promise<ReportData[]> {
    const response = await api.get('/api/v2/reports/');
    return response.data;
  }

  // Buscar relatório específico
  async getReport(reportId: string): Promise<ReportData> {
    const response = await api.get(`/api/v2/reports/${reportId}`);
    return response.data;
  }

  // Download de relatório
  async downloadReport(reportId: string): Promise<Blob> {
    const response = await api.get(`/api/v2/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Download do relatório XLSX de enriquecimento de ativos
   * Usa o contexto da empresa do usuário autenticado no backend
   */
  async downloadAssetsEnrichmentXlsx(options?: {
    rdap_timeout?: number;
    enum_timeout_per_domain?: number;
    max_root_domains?: number;
    max_total_subdomains?: number;
    poll_interval_ms?: number;
    max_wait_ms?: number;
  }): Promise<void> {
    const params = this.buildAssetsEnrichmentParams(options);
    const startResp = await api.post('/api/v2/reports/assets-enrichment/xlsx/async-start', null, { params });
    const jobId = (startResp.data?.job_id || startResp.data?.jobId) as string;
    if (!jobId) {
      throw new Error('Falha ao iniciar job de relatório.');
    }
    const pollInterval = options?.poll_interval_ms ?? 3000;
    const maxWait = options?.max_wait_ms ?? (15 * 60 * 1000);
    const initGraceMs = 20000;
    await this.waitForEnrichmentCompletion(jobId, pollInterval, maxWait, initGraceMs);

    const response = await api.get(`/api/v2/reports/assets-enrichment/xlsx/async-download/${jobId}`, { responseType: 'blob' });
    const contentType = response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([response.data], { type: contentType });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename = this.parseDispositionFilename(disposition) ?? `relatorio_assets_${Date.now()}.xlsx`;
    this.triggerDownload(blob, filename);
  }

  private buildAssetsEnrichmentParams(options?: {
    rdap_timeout?: number;
    enum_timeout_per_domain?: number;
    max_root_domains?: number;
    max_total_subdomains?: number;
  }): Record<string, any> {
    const p: Record<string, any> = {};
    if (options?.rdap_timeout !== undefined) p['rdap_timeout'] = options.rdap_timeout;
    if (options?.enum_timeout_per_domain !== undefined) p['enum_timeout_per_domain'] = options.enum_timeout_per_domain;
    if (options?.max_root_domains !== undefined) p['max_root_domains'] = options.max_root_domains;
    if (options?.max_total_subdomains !== undefined) p['max_total_subdomains'] = options.max_total_subdomains;
    return p;
  }

  private async waitForEnrichmentCompletion(jobId: string, pollInterval: number, maxWait: number, initGraceMs: number): Promise<void> {
    const startTime = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await this.delay(pollInterval);
      try {
        const statusResp = await api.get(`/api/v2/reports/assets-enrichment/xlsx/async-status/${jobId}`);
        const status = statusResp.data?.status as string;
        const errorMsg = statusResp.data?.error as string | undefined;
        if (status === 'completed') return;
        if (status === 'failed') throw new Error(errorMsg || 'Job falhou.');
        if (Date.now() - startTime > maxWait) throw new Error('Tempo máximo de espera excedido para geração do relatório.');
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        if (httpStatus === 404 && Date.now() - startTime < initGraceMs) continue;
        throw err;
      }
    }
  }

  private parseDispositionFilename(disposition?: string | null): string | null {
    if (!disposition) return null;
    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match && match[1] ? match[1] : null;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  /**
   * Generate and download CSV report of leaked credentials
   * Automatically filters by user's email domain
   */
  async downloadLeakedCredentialsCSV(filters?: { severity?: string[]; sources?: string[] }): Promise<void> {
    try {
      const params = new URLSearchParams();
      params.append('download', 'true');

      if (filters?.severity) {
        filters.severity.forEach(s => params.append('severity', s));
      }
      if (filters?.sources) {
        filters.sources.forEach(s => params.append('source', s));
      }

      const response = await api.get(`/api/v2/reports/leaked-credentials/csv?${params.toString()}`, {
        responseType: 'blob',
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'leaked_credentials.csv';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV report:', error);
      throw error;
    }
  }

  /**
   * Get CSV content as text for leaked credentials
   * Automatically filters by user's email domain
   */
  async getLeakedCredentialsCsv(): Promise<string> {
    try {
      const params = new URLSearchParams();
      params.append('download', 'false');

      const response = await api.get(`/api/v2/reports/leaked-credentials/csv?${params.toString()}`, {
        responseType: 'text',
      });

      return response.data;
    } catch (error) {
      console.error('Error getting CSV content:', error);
      throw error;
    }
  }

  /**
   * Preview leaked credentials report data
   * Automatically filters by user's email domain
   */
  async previewLeakedCredentialsReport(limit: number = 10): Promise<any> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());

      const response = await api.get(`/api/v2/reports/leaked-credentials/preview?${params.toString()}`);

      return response.data;
    } catch (error) {
      console.error('Error previewing report:', error);
      throw error;
    }
  }

  /**
   * Get leaked credentials statistics
   * Automatically filters by user's email domain
   */
  async getLeakedCredentialsStats(): Promise<any> {
    try {
      const response = await api.get('/api/v2/leaked-credentials/stats');

      return response.data;
    } catch (error) {
      console.error('Error getting report stats:', error);
      throw error;
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  }
}

export const reportsService = new ReportsService();
export default reportsService;
